import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheckIcon } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Etiqueta } from "@/components/ui/etiqueta";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { useCalidad, useCorrerCalidad, useResolverObservacion } from "@/hooks/useEmision";
import { ApiError, getApiErrorMessage } from "@/lib/api";
import { formatearFechaYHora } from "@/lib/fechas";
import {
  ETIQUETA_DE_ORIGEN,
  agruparObservaciones,
  frescuraDeLaRevision,
  type CondicionesDeEmision,
  type ObservacionDeCalidad,
} from "@/lib/emision";

interface RevisionDeCalidadProps {
  informeId: number;
  condiciones: CondicionesDeEmision | undefined;
}

/**
 * La revisión de calidad del documento armado. SEÑALA y nunca edita: cada
 * hallazgo cita el fragmento literal del documento y una persona decide si lo
 * corrigió, lo descarta (con motivo) o lo reabre. Las graves abiertas bloquean
 * la emisión; una revisión anterior al último cambio tampoco vale.
 */
export function RevisionDeCalidad({ informeId, condiciones }: RevisionDeCalidadProps) {
  const calidad = useCalidad(informeId);
  const correr = useCorrerCalidad(informeId);
  const [aDescartar, setADescartar] = useState<ObservacionDeCalidad | null>(null);

  const corrida = calidad.data?.corrida ?? null;
  const corriendo = corrida?.estado === "en_curso";

  function ejecutar() {
    correr.mutate(undefined, {
      onError: (error) => {
        // Ya hay una corriendo: no es un fallo, la pantalla se engancha con ella.
        if (error instanceof ApiError && error.status === 409) {
          void calidad.refetch();
          toast.info("Ya hay una revisión en curso. Esperamos a que termine.");
          return;
        }
        toast.error(getApiErrorMessage(error, "No pudimos iniciar la revisión. Inténtalo de nuevo."));
      },
    });
  }

  const frescura = condiciones ? frescuraDeLaRevision(condiciones) : null;
  const grupos = agruparObservaciones(calidad.data?.observaciones ?? []);

  return (
    <section id="revision-de-calidad" aria-label="Revisión de calidad" className="bg-card space-y-4 rounded-[1.25rem] border-[1.5px] p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h3 className="text-body text-ink font-extrabold">Revisión de calidad</h3>
          <p className="text-ink-muted max-w-prose text-xs font-semibold text-pretty">
            Una revisión independiente lee el documento completo y señala contradicciones, notas internas y datos que no
            coinciden. No cambia nada: tú decides qué hacer con cada hallazgo.
          </p>
          {frescura ? (
            <p className={frescura.desactualizada ? "text-warning-texto text-xs font-bold" : "text-ink-muted text-xs font-semibold"}>
              {frescura.texto}
            </p>
          ) : null}
        </div>

        <Button type="button" variant="outline" disabled={corriendo || correr.isPending} onClick={ejecutar}>
          {corriendo || correr.isPending ? <Spinner aria-hidden /> : <ShieldCheckIcon aria-hidden />}
          {corriendo ? "Revisando…" : corrida ? "Volver a revisar" : "Revisar el documento"}
        </Button>
      </div>

      {corriendo && corrida ? (
        <p role="status" className="text-ink-muted text-xs font-semibold">
          {corrida.paso ? `Paso ${corrida.paso.numero} de ${corrida.paso.de}` : "Preparando la revisión"} ·{" "}
          {corrida.porcentaje}% · puede tardar un minuto.
        </p>
      ) : null}

      {corrida?.estado === "error" && corrida.error ? (
        <Alert variant="destructive">
          <AlertTitle>La revisión no terminó</AlertTitle>
          <AlertDescription>
            <p>{corrida.error.mensaje}</p>
          </AlertDescription>
        </Alert>
      ) : null}

      {calidad.isPending ? <Skeleton className="h-20 w-full rounded-xl" /> : null}

      {calidad.isError ? (
        <Alert variant="destructive">
          <AlertDescription>
            <p>{getApiErrorMessage(calidad.error, "No pudimos cargar la revisión de calidad.")}</p>
          </AlertDescription>
        </Alert>
      ) : null}

      {calidad.data ? (
        <div className="space-y-4">
          {corrida?.estado === "ok" && calidad.data.observaciones.length === 0 ? (
            <p className="text-success-soft-foreground bg-success-soft rounded-xl px-4 py-3 text-xs font-bold">
              La revisión no encontró nada que señalar.
            </p>
          ) : null}

          <GrupoDeObservaciones
            titulo={`Graves · ${grupos.graves.length}`}
            vacio="No hay observaciones graves abiertas."
            observaciones={grupos.graves}
            informeId={informeId}
            onDescartar={setADescartar}
            mostrarVacio={corrida !== null}
          />
          <GrupoDeObservaciones
            titulo={`Advierten · ${grupos.advierten.length}`}
            vacio="No hay advertencias abiertas."
            observaciones={grupos.advierten}
            informeId={informeId}
            onDescartar={setADescartar}
            mostrarVacio={corrida !== null}
          />
          {grupos.resueltas.length > 0 ? (
            <GrupoDeObservaciones
              titulo={`Resueltas · ${grupos.resueltas.length}`}
              vacio=""
              observaciones={grupos.resueltas}
              informeId={informeId}
              onDescartar={setADescartar}
              colapsable
            />
          ) : null}
          {grupos.yaNoAparecen.length > 0 ? (
            <GrupoDeObservaciones
              titulo={`Ya no aparecen · ${grupos.yaNoAparecen.length}`}
              vacio=""
              observaciones={grupos.yaNoAparecen}
              informeId={informeId}
              onDescartar={setADescartar}
              colapsable
              atenuado
            />
          ) : null}
        </div>
      ) : null}

      <DialogoDeDescarte informeId={informeId} observacion={aDescartar} onCerrar={() => setADescartar(null)} />
    </section>
  );
}

interface GrupoDeObservacionesProps {
  titulo: string;
  vacio: string;
  observaciones: ObservacionDeCalidad[];
  informeId: number;
  onDescartar: (observacion: ObservacionDeCalidad) => void;
  mostrarVacio?: boolean;
  colapsable?: boolean;
  atenuado?: boolean;
}

function GrupoDeObservaciones({
  titulo,
  vacio,
  observaciones,
  informeId,
  onDescartar,
  mostrarVacio = false,
  colapsable = false,
  atenuado = false,
}: GrupoDeObservacionesProps) {
  if (observaciones.length === 0) {
    return mostrarVacio ? (
      <div className="space-y-1">
        <h4 className="text-xs font-bold">{titulo}</h4>
        <p className="text-ink-medio text-xs font-semibold">{vacio}</p>
      </div>
    ) : null;
  }

  const lista = (
    <ul className="flex flex-col gap-2">
      {observaciones.map((observacion) => (
        <li key={observacion.id}>
          <TarjetaDeObservacion
            informeId={informeId}
            observacion={observacion}
            onDescartar={onDescartar}
            atenuada={atenuado}
          />
        </li>
      ))}
    </ul>
  );

  if (colapsable) {
    return (
      <details className="group">
        <summary className="text-xs font-bold cursor-pointer select-none">{titulo}</summary>
        <div className="mt-2">{lista}</div>
      </details>
    );
  }

  return (
    <div className="space-y-2">
      <h4 className="text-xs font-bold">{titulo}</h4>
      {lista}
    </div>
  );
}

function TarjetaDeObservacion({
  informeId,
  observacion,
  onDescartar,
  atenuada,
}: {
  informeId: number;
  observacion: ObservacionDeCalidad;
  onDescartar: (observacion: ObservacionDeCalidad) => void;
  atenuada: boolean;
}) {
  const resolver = useResolverObservacion(informeId);

  function decidir(estado: "abierta" | "corregida") {
    resolver.mutate(
      { observacionId: observacion.id, estado },
      { onError: (error) => toast.error(getApiErrorMessage(error, "No pudimos guardar tu decisión.")) },
    );
  }

  const abierta = observacion.estado === "abierta";

  return (
    <article
      className={`bg-superficie-tenue border-border space-y-2 rounded-[0.875rem] border-[1.5px] p-3.5 ${atenuada ? "opacity-60" : ""}`}
      aria-label={`Observación ${observacion.id}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Etiqueta tono={observacion.gravedad === "grave" ? "danger" : "warning"}>
          {observacion.gravedad === "grave" ? "Grave" : "Advierte"}
        </Etiqueta>
        <Etiqueta tono="neutral">{ETIQUETA_DE_ORIGEN[observacion.origen]}</Etiqueta>
        {observacion.seccion?.titulo ? (
          <span className="text-ink-secundario text-xs font-bold">{observacion.seccion.titulo}</span>
        ) : null}
        {!abierta ? <Etiqueta tono="info">{ETIQUETA_DE_ESTADO[observacion.estado]}</Etiqueta> : null}
      </div>

      {observacion.fragmento ? (
        <blockquote className="text-ink border-input border-l-[3px] pl-3 text-xs font-semibold italic">«{observacion.fragmento}»</blockquote>
      ) : null}
      {observacion.fragmento_relacionado ? (
        <blockquote className="text-ink-secundario border-input border-l-[3px] pl-3 text-xs font-semibold italic">
          Contra: «{observacion.fragmento_relacionado}»
        </blockquote>
      ) : null}
      {observacion.explicacion ? <p className="text-ink-secundario text-xs font-semibold text-pretty">{observacion.explicacion}</p> : null}
      {observacion.sugerencia ? (
        <p className="text-ink-secundario text-xs font-semibold text-pretty">
          <span className="font-bold">Sugerencia: </span>
          {observacion.sugerencia}
        </p>
      ) : null}

      {observacion.estado === "descartada" && observacion.motivo ? (
        <p className="text-ink-muted text-xs font-semibold">
          Descartada{observacion.resuelta_en ? ` el ${formatearFechaYHora(observacion.resuelta_en)}` : ""}
          {observacion.resuelta_por?.nombre ? ` por ${observacion.resuelta_por.nombre}` : ""}: {observacion.motivo}
        </p>
      ) : null}

      {observacion.estado !== "ya_no_aparece" ? (
        <div className="flex flex-wrap gap-2">
          {abierta ? (
            <>
              <Button
                type="button"
                size="sm"
                variant="fuerte"
                className="h-auto rounded-[0.625rem] px-3 py-1.5"
                disabled={resolver.isPending}
                onClick={() => decidir("corregida")}
              >
                Marcar como corregida
              </Button>
              <Button type="button" size="sm" variant="outline" disabled={resolver.isPending} onClick={() => onDescartar(observacion)}>
                Descartar
              </Button>
            </>
          ) : (
            <Button type="button" size="sm" variant="outline" disabled={resolver.isPending} onClick={() => decidir("abierta")}>
              Reabrir
            </Button>
          )}
        </div>
      ) : null}
    </article>
  );
}

const ETIQUETA_DE_ESTADO: Record<ObservacionDeCalidad["estado"], string> = {
  abierta: "Abierta",
  corregida: "Corregida",
  descartada: "Descartada",
  ya_no_aparece: "Ya no aparece",
};

function DialogoDeDescarte({
  informeId,
  observacion,
  onCerrar,
}: {
  informeId: number;
  observacion: ObservacionDeCalidad | null;
  onCerrar: () => void;
}) {
  return (
    <Dialog open={observacion !== null} onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent className="sm:max-w-[30rem]">
        {/* La `key` reinicia el motivo con cada observación: nada se arrastra de la anterior. */}
        {observacion ? <FormularioDeDescarte key={observacion.id} informeId={informeId} observacion={observacion} onCerrar={onCerrar} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function FormularioDeDescarte({
  informeId,
  observacion,
  onCerrar,
}: {
  informeId: number;
  observacion: ObservacionDeCalidad;
  onCerrar: () => void;
}) {
  const resolver = useResolverObservacion(informeId);
  const [motivo, setMotivo] = useState("");
  const sinMotivo = motivo.trim() === "";

  function confirmar() {
    if (sinMotivo) return;

    resolver.mutate(
      { observacionId: observacion.id, estado: "descartada", motivo: motivo.trim() },
      {
        onSuccess: onCerrar,
        onError: (error) => toast.error(getApiErrorMessage(error, "No pudimos descartar la observación.")),
      },
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Descartar observación</DialogTitle>
        <DialogDescription>
          Cuéntanos por qué no corresponde. El motivo queda anotado con tu nombre y no se puede dejar en blanco.
        </DialogDescription>
      </DialogHeader>

      <Textarea
        aria-label="Motivo del descarte"
        value={motivo}
        onChange={(evento) => setMotivo(evento.target.value)}
        placeholder="Ej.: el tasador confirmó la superficie en terreno"
        rows={4}
      />

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCerrar}>
          Cancelar
        </Button>
        <Button type="button" variant="fuerte" disabled={sinMotivo || resolver.isPending} onClick={confirmar}>
          Descartar
        </Button>
      </DialogFooter>
    </>
  );
}
